import { useState } from 'react';
import { useTranslation } from '../../components/Translation';
import { events, LabEvent } from '../../constants/events/events';
import EventCard from './components/EventCard';
import EventsHero from './components/EventsHero';
import EventModal from './components/EventModal';

export default function EventsPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<LabEvent | null>(null);
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("");
  const [order, setOrder] = useState<"desc" | "asc">("desc");

  const getTitle = (e: LabEvent) =>
    t(`eventos.${e.titleKey}`) !== `eventos.${e.titleKey}` ? t(`eventos.${e.titleKey}`) : e.titleText;

  // Fecha local YYYY-MM-DD; un evento de hoy sigue siendo próximo
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  // El estado se deriva de la fecha: si ya pasó, siempre es "pasado"
  const withStatus = events.map(e => ({
    ...e,
    status: e.date < today ? "pasado" : e.status === "destacado" ? "destacado" : "proximo",
  } as LabEvent));

  const q = query.trim().toLowerCase();
  const filtered = withStatus
    .filter(e => !q || getTitle(e).toLowerCase().includes(q))
    .filter(e => !month || e.date.startsWith(month))
    .sort((a, b) => order === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date));

  const featured = filtered.find(e => e.status === "destacado");
  const upcoming = filtered.filter(e => e.status === "proximo");
  const past = filtered.filter(e => e.status === "pasado");

  return (
    <div className="min-h-screen" style={{
      background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)"
    }}>
      {/* Header */}
      <div className="bg-azul-uady text-white py-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 70% 50%, #c89211 0%, transparent 50%)'
        }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 bg-dorado-uady/20 border border-dorado-uady/40 text-dorado-uady text-micro font-semibold px-3 py-1 rounded-full mb-4">
            {t('eventos.badge_seccion')}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
            {t('eventos.titulo')}
          </h1>
          <p className="text-white/60 text-mini leading-relaxed max-w-xl">
            {t('eventos.subtitulo')}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-14">

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('eventos.filtro_buscar')}
            aria-label={t('eventos.filtro_buscar')}
            className="flex-1 px-4 py-2 rounded-xl border border-gray-200 bg-white text-mini focus:outline-none focus:border-azul-uady"
          />
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            aria-label={t('eventos.filtro_fecha')}
            className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-mini focus:outline-none focus:border-azul-uady"
          />
          <select
            value={order}
            onChange={e => setOrder(e.target.value as "desc" | "asc")}
            aria-label={t('eventos.filtro_orden')}
            className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-mini focus:outline-none focus:border-azul-uady"
          >
            <option value="desc">{t('eventos.orden_reciente')}</option>
            <option value="asc">{t('eventos.orden_antiguo')}</option>
          </select>
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-gray-500 text-mini">{t('eventos.sin_resultados')}</p>
        )}

        {/* Evento destacado */}
        {featured && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-1 h-6 bg-dorado-uady rounded" />
              <h2 className="text-2xl font-bold text-azul-uady">
                {t('eventos.seccion_destacado')}
              </h2>
            </div>
            <EventsHero event={featured} onClick={() => setSelected(featured)} />
          </section>
        )}

        {/* Próximos eventos */}
        {upcoming.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-1 h-6 bg-dorado-uady rounded" />
              <h2 className="text-2xl font-bold text-azul-uady">
                {t('eventos.seccion_proximos')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((ev) => (
                <EventCard key={ev.id} event={ev} onClick={() => setSelected(ev)} />
              ))}
            </div>
          </section>
        )}

        {/* Eventos pasados */}
        {past.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-1 h-6 bg-dorado-uady rounded" />
              <h2 className="text-2xl font-bold text-azul-uady">
                {t('eventos.seccion_pasados')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((ev) => (
                <EventCard key={ev.id} event={ev} onClick={() => setSelected(ev)} />
              ))}
            </div>
          </section>
        )}
      </div>

      {selected && (
        <EventModal event={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
